import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import store from '@/store'
import { debounce } from 'lodash'
import axios from '@/libs/axios'
import useFilterCounter from '@/components/Filters/useFiltersCount'
import { getPlanningStatusLabelKey } from './usePlanningStatus'
import { normalizePlanningActivitySlugFromApi } from './fetchPlanningFirstReviewersByActivityType'

const ACTIVITY_SLUG_TO_I18N_KEY = {
  risk: 'planning.evaluacion_riesgo',
  operational_procedure: 'planning.ejercicio_operativo',
  simulacrum: 'planning.simulacro',
  security_self_audit: 'planning.auto_evaluacion_seguridad',
}

const BASE_PATH = 'v1/planning'

function formatDateValue(value) {
  if (!value) return null
  const str = String(value).slice(0, 10)
  if (str.length < 10) return null
  return str
}

function capitalizeFirst(value) {
  if (value == null || value === '' || value === '-') return value
  const str = String(value).trim()
  if (!str) return '-'
  return str.charAt(0).toUpperCase() + str.slice(1)
}

function getActivityTypeDisplay(planning) {
  if (!planning) return '-'
  const at = planning.activity_type
  let raw = null
  if (typeof at === 'string') raw = at
  else if (at && at.name) raw = at.name
  return raw ? capitalizeFirst(raw) : '-'
}

function getActivityTypeTranslateKey(planning) {
  if (!planning) return null
  const at = planning.activity_type
  let raw = null
  if (typeof at === 'string') raw = at
  else if (at && at.name) raw = at.name
  if (raw == null || String(raw).trim() === '') return null
  const slug = normalizePlanningActivitySlugFromApi(raw)
  if (!slug) return null
  return ACTIVITY_SLUG_TO_I18N_KEY[slug] || null
}

function toStringOrDash(value) {
  if (value == null || value === '') return '-'
  return String(value).trim() || '-'
}

function getPlanningStatusTitle(status, translate) {
  if (!status || typeof status !== 'object') return '-'
  const key = getPlanningStatusLabelKey(status)
  if (key) {
    const t = translate(key)
    if (t && t !== key) return t
  }
  if (status.name != null && String(status.name).trim() !== '') return String(status.name).trim()
  if (status.code != null) return String(status.code).trim()
  return '-'
}

function getActorPrimary(value) {
  if (!value) return '-'
  if (typeof value === 'string') return value || '-'
  return value.name || value.username || value.email || '-'
}

function getActorSecondary(value) {
  if (!value) return ''
  if (typeof value === 'string') return ''
  return value.username || value.email || ''
}

function mapPlanningListToRows(planningList) {
  const list = Array.isArray(planningList) ? planningList : []
  return list.map((planning) => {
    const planningId = planning && planning.id != null ? planning.id : null
    const dateFrom = planning && planning.date_from ? planning.date_from : null
    const dateTo = planning && planning.date_to ? planning.date_to : null
    return {
      id: planningId,
      planning_id_for_link: planningId,
      planning_id_display: planningId != null ? String(planningId) : '-',
      activity_type_display: getActivityTypeDisplay(planning),
      activity_type_translate_key: getActivityTypeTranslateKey(planning),
      sector_name: toStringOrDash(planning && planning.sectors),
      region_name: toStringOrDash(planning && planning.regions),
      rems_display: toStringOrDash(planning && planning.rems),
      unit_name: toStringOrDash(planning && planning.units),
      facility_category_name: toStringOrDash(planning && planning.facility_categories),
      start_date: dateFrom,
      target_date: dateTo,
      start_date_formatted: formatDateValue(dateFrom) ? moment(dateFrom).format('DD/MM/YYYY') : '-',
      target_date_formatted: formatDateValue(dateTo) ? moment(dateTo).format('DD/MM/YYYY') : '-',
      planning_status: planning && planning.status != null ? planning.status : null,
      last_status: planning && planning.last_status != null ? planning.last_status : null,
      assigned_to: planning && planning.assigned_to != null ? planning.assigned_to : null,
      in_review_by: planning && planning.in_review_by != null ? planning.in_review_by : null,
      accepted_or_cancelled_by: planning && planning.accepted_or_cancelled_by != null ? planning.accepted_or_cancelled_by : null,
    }
  })
}

export default function useList() {
  const refListTable = ref(null)
  const toast = useToast()

  const tableColumns = [
    { label: 'actions.name', key: 'actions', sortable: false },
    { label: 'planning.planning_id', key: 'planning_id_display', sortable: false },
    { label: 'planning.activity_type', key: 'activity_type_display', sortable: false },
    { label: 'planning.business_division', key: 'sector_name', sortable: false },
    { label: 'planning.federal_entity', key: 'region_name', sortable: false },
    { label: 'planning.rems', key: 'rems_display', sortable: false },
    { label: 'planning.property_name', key: 'unit_name', sortable: false },
    { label: 'planning.property_type', key: 'facility_category_name', sortable: false },
    { label: 'planning.fecha_inicio', key: 'start_date_formatted', sortable: false },
    { label: 'planning.fecha_fin', key: 'target_date_formatted', sortable: false },
    { label: 'planning.status', key: 'planning_status', sortable: false },
    { label: 'planning.list_col_assigned_to', key: 'assigned_to', sortable: false },
    { label: 'planning.list_col_in_review_by', key: 'in_review_by', sortable: false },
    { label: 'planning.list_col_accepted_cancelled', key: 'accepted_or_cancelled_by', sortable: false },
  ]

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref(null)
  const isSortDirDesc = ref(true)

  const activityTypeFilter = ref(null)
  const sectorIdsFilter = ref([])
  const regionIdsFilter = ref([])
  const remsFilter = ref(null)
  const unitNameFilter = ref(null)
  const facilityCategoryFilter = ref(null)
  const fechaInicioFilter = ref(null)
  const fechaFinFilter = ref(null)
  const statusFilter = ref(null)

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const filters = ref({
    activity_type: null,
    sector_ids: [],
    region_ids: [],
    rems: null,
    unit_name: null,
    facility_category: null,
    fecha_inicio: null,
    fecha_fin: null,
    status: null,
  })
  const filtersCount = useFilterCounter(filters)

  function appendScalarParam(target, key, value) {
    if (value == null) return
    const trimmed = String(value).trim()
    if (!trimmed) return
    target[key] = trimmed
  }

  function buildQueryParams() {
    const base = {
      page: currentPage.value,
      per_page: perPage.value,
    }
    appendScalarParam(base, 'q', searchQuery.value)
    if (activityTypeFilter.value != null && String(activityTypeFilter.value).trim() !== '') {
      base.activity_type = String(activityTypeFilter.value).trim()
    }
    const sectors = Array.isArray(sectorIdsFilter.value) ? sectorIdsFilter.value.filter((id) => id != null && id !== '') : []
    if (sectors.length > 0) base.sector_ids = sectors.join(',')
    const regions = Array.isArray(regionIdsFilter.value) ? regionIdsFilter.value.filter((id) => id != null && id !== '') : []
    if (regions.length > 0) base.region_ids = regions.join(',')
    appendScalarParam(base, 'rems', remsFilter.value)
    appendScalarParam(base, 'unit_name', unitNameFilter.value)
    appendScalarParam(base, 'facility_category', facilityCategoryFilter.value)
    appendScalarParam(base, 'status', statusFilter.value)
    const fromRaw = fechaInicioFilter.value
    const toRaw = fechaFinFilter.value
    if (fromRaw != null && String(fromRaw).trim() !== '') {
      base.fecha_inicio = String(fromRaw).slice(0, 10)
    }
    if (toRaw != null && String(toRaw).trim() !== '' && (!fromRaw || String(fromRaw).slice(0, 10) <= String(toRaw).slice(0, 10))) {
      base.fecha_fin = String(toRaw).slice(0, 10)
    }
    return base
  }

  const fetchList = () => {
    return new Promise((resolve, reject) => {
      const params = buildQueryParams()

      axios
        .get(BASE_PATH, { params })
        .then((response) => {
          const data = response.data
          let planningList = []
          const inner = data.data
          if (Array.isArray(inner)) {
            planningList = inner
          } else if (inner && Array.isArray(inner.data)) {
            planningList = inner.data
          } else if (inner && typeof inner === 'object' && inner.id != null) {
            planningList = [inner]
          }
          const rows = mapPlanningListToRows(planningList)
          let meta = {}
          if (data.meta != null) meta = data.meta
          else if (inner && typeof inner === 'object' && inner.meta != null) meta = inner.meta
          const total = meta.total != null ? meta.total : (data.total != null ? data.total : rows.length)
          totalList.value = total
          resolve(rows)
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error loading planning',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          reject()
        })
    })
  }

  const formatDate = (value) => {
    if (!value) return '-'
    return moment(value).format('DD/MM/YYYY')
  }

  const refetchData = () => {
    if (refListTable.value) refListTable.value.refresh()
  }

  const buildFilters = () => {
    filters.value = {
      activity_type: activityTypeFilter.value,
      sector_ids: sectorIdsFilter.value,
      region_ids: regionIdsFilter.value,
      rems: remsFilter.value,
      unit_name: unitNameFilter.value,
      facility_category: facilityCategoryFilter.value,
      fecha_inicio: fechaInicioFilter.value,
      fecha_fin: fechaFinFilter.value,
      status: statusFilter.value,
    }
    currentPage.value = 1
    refetchData()
  }

  const clearSearchFilters = () => {
    activityTypeFilter.value = null
    sectorIdsFilter.value = []
    regionIdsFilter.value = []
    remsFilter.value = null
    unitNameFilter.value = null
    facilityCategoryFilter.value = null
    fechaInicioFilter.value = null
    fechaFinFilter.value = null
    statusFilter.value = null
    filters.value = {
      activity_type: null,
      sector_ids: [],
      region_ids: [],
      rems: null,
      unit_name: null,
      facility_category: null,
      fecha_inicio: null,
      fecha_fin: null,
      status: null,
    }
    currentPage.value = 1
    refetchData()
  }

  watch([perPage], () => refetchData())
  watch(
    [searchQuery],
    debounce(() => {
      currentPage.value = 1
      refetchData()
    }, 500)
  )

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    refetchData,
    formatDate,
    activityTypeFilter,
    sectorIdsFilter,
    regionIdsFilter,
    remsFilter,
    unitNameFilter,
    facilityCategoryFilter,
    fechaInicioFilter,
    fechaFinFilter,
    statusFilter,
    filters,
    filtersCount,
    buildFilters,
    clearSearchFilters,
    getPlanningStatusTitle,
    getActorPrimary,
    getActorSecondary,
  }
}
